import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const DocumentManagementModule = () => {
  const [activeSection, setActiveSection] = useState("document-repository");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleDocuments = () => navigate('/access-recruit');

  const sectionContent = {
    "document-repository": {
      title: "Centralized Document",
      description: "Create a secure, organized central repository for all HR documents with intelligent categorization and search capabilities. Store employee records, contracts, policies, certificates, and compliance documents in structured digital formats. Implement version control, audit trails, and access permissions based on roles and departments. Enable full-text search, metadata tagging, and document linking for efficient information retrieval and management across the employee lifecycle."
    },
    "document-automation": {
      title: "Intelligent Document",
      description: "Automate document creation, generation, and distribution throughout HR processes. Create smart templates for offer letters, contracts, policy acknowledgments, certificates, and reports with dynamic field population. Generate personalized documents in multiple formats with automated distribution to relevant stakeholders. Implement conditional logic for document variations and maintain consistency across all organizational communications while reducing manual document preparation time."
    },
    "e-signature-workflows": {
      title: "Electronic Signature",
      description: "Streamline document approvals and execution with integrated electronic signature capabilities. Send documents for e-signature with automated routing to employees, managers, and external parties. Track signature status, send reminders, and manage signing sequences. Ensure legal compliance with audit trails, timestamping, and certificate-based authentication. Integrate with HR processes for seamless document execution from offer acceptance to policy acknowledgments and contract renewals."
    },
    "compliance-documentation": {
      title: "Compliance & Audit Document",
      description: "Maintain comprehensive compliance documentation with automated tracking, retention policies, and audit readiness. Manage document retention schedules, expiration dates, and archiving requirements based on regulatory standards. Generate compliance reports, audit trails, and certification documentation. Implement document classification, sensitivity labeling, and access controls for confidential information. Ensure GDPR, HIPAA, and other regulatory compliance through systematic document management practices."
    },
    "document-analytics": {
      title: "Document Analytics & Insights",
      description: "Gain visibility into document usage, compliance status, and management efficiency with advanced analytics. Track document lifecycle metrics including creation rates, approval times, signature completion, and access patterns. Identify document bottlenecks, compliance gaps, and process inefficiencies. Generate usage reports, compliance dashboards, and predictive analytics for document management optimization. Measure document management impact on HR efficiency and regulatory compliance."
    }
  };

  const sections = [
    { id: "document-repository", label: "REPOSITORY" },
    { id: "document-automation", label: "AUTOMATION" },
    { id: "e-signature-workflows", label: "E-SIGNATURES" },
    { id: "compliance-documentation", label: "COMPLIANCE" },
    { id: "document-analytics", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Document Repository */}
        <section
          ref={(el) => (sectionsRef.current["document-repository"] = el)}
          data-section="document-repository"
          className={`resto-section ${visibleSections.has("document-repository") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Repository</div>
            <h2 className="resto-section-title">
              {sectionContent["document-repository"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["document-repository"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleDocuments}>Access Repository</button>
              <button className="resto-secondary-btn" onClick={handleDocuments}>Upload Documents</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/dot1.png" className="resto-real-image" alt="Document Repository" />
          </div>
        </section>

        {/* 2. Document Automation */}
        <section
          ref={(el) => (sectionsRef.current["document-automation"] = el)}
          data-section="document-automation"
          className={`resto-section resto-reverse ${visibleSections.has("document-automation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Automation</div>
            <h2 className="resto-section-title">
              {sectionContent["document-automation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["document-automation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleDocuments}>Create Templates</button>
              <button className="resto-secondary-btn" onClick={handleDocuments}>Generate Documents</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/dot2.png" className="resto-real-image" alt="Document Automation" />
          </div>
        </section>

        <CTA/>

        {/* 3. E-Signature Workflows */}
        <section
          ref={(el) => (sectionsRef.current["e-signature-workflows"] = el)}
          data-section="e-signature-workflows"
          className={`resto-section ${visibleSections.has("e-signature-workflows") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">E-Signatures</div>
            <h2 className="resto-section-title">
              {sectionContent["e-signature-workflows"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["e-signature-workflows"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleDocuments}>Send for Signature</button>
              <button className="resto-secondary-btn" onClick={handleDocuments}>Track Signatures</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/dot3.png" className="resto-real-image" alt="E-Signature Workflows" />
          </div>
        </section>

        {/* 4. Compliance Documentation */}
        <section
          ref={(el) => (sectionsRef.current["compliance-documentation"] = el)}
          data-section="compliance-documentation"
          className={`resto-section resto-reverse ${visibleSections.has("compliance-documentation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Compliance</div>
            <h2 className="resto-section-title">
              {sectionContent["compliance-documentation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["compliance-documentation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleDocuments}>Manage Compliance</button>
              <button className="resto-secondary-btn" onClick={handleDocuments}>Run Audits</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/dot4.png" className="resto-real-image" alt="Compliance Documentation" />
          </div>
        </section>

        {/* 5. Document Analytics */}
        <section
          ref={(el) => (sectionsRef.current["document-analytics"] = el)}
          data-section="document-analytics"
          className={`resto-section ${visibleSections.has("document-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["document-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["document-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleDocuments}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleDocuments}>Generate Reports</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/dot5.png" className="resto-real-image" alt="Document Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default DocumentManagementModule;